import { AlertMessage } from "../../components/alerts";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Trophy,
  Users,
  Files,
  Send,
  ListOrdered,
  ChartNoAxesColumn,
  ShieldUser,
  PanelLeft,
  X,
  LogOut,
  ShieldCheck,
  CircleCheck,
  CircleAlert,
} from "lucide-react";
import type { ContestStateDto, HealthDto } from "@compila/shared";
import { api } from "../../lib/api.js";
import { useEventStream, useSession } from "../../lib/hooks.js";
import { Button } from "../../components/motion/button/base";
import {
  AnimatedSidebar,
  AnimatedSidebarProvider,
  AnimatedSidebarInset,
  AnimatedSidebarHeader,
  AnimatedSidebarContent,
  AnimatedSidebarFooter,
  AnimatedSidebarMenu,
  AnimatedSidebarMenuItem,
  AnimatedSidebarMenuButton,
  AnimatedSidebarGroup,
  AnimatedSidebarGroupLabel,
  AnimatedSidebarTrigger,
  AnimatedSidebarClose,
  useAnimatedSidebar,
} from "../../components/motion/animated-sidebar";

const navigation = [
  { path: "/admin/contest", label: "Competição", icon: Trophy },
  { path: "/admin/equipes", label: "Equipes", icon: Users },
  { path: "/admin/problemas", label: "Problemas", icon: Files },
  { path: "/admin/submissoes", label: "Submissões", icon: Send },
  { path: "/admin/fila", label: "Fila", icon: ListOrdered },
  { path: "/admin/placar", label: "Placar", icon: ChartNoAxesColumn },
  { path: "/admin/administradores", label: "Admins", icon: ShieldUser },
];

function AdminSidebar() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const session = useSession();
  const queryClient = useQueryClient();
  const { state, isMobile } = useAnimatedSidebar();
  const expanded = state === "expanded" || isMobile;
  const logout = useMutation({
    mutationFn: () => api.post("/auth/logout"),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["me"] });
      navigate("/login");
    },
  });
  return (
    <AnimatedSidebar ariaLabel="Navegação administrativa">
      <AnimatedSidebarHeader className="h-20 justify-center px-4">
        <div className="flex items-center gap-3 overflow-hidden">
          <img
            src="/logo.svg"
            alt="Maratona Nerds"
            className="size-9 shrink-0 rounded-xl object-contain"
          />
          {expanded && (
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold">Maratona Nerds</p>
              <p className="text-xs text-muted-foreground">Administração</p>
            </div>
          )}
          {isMobile && (
            <AnimatedSidebarClose aria-label="Fechar navegação" className="ml-auto">
              <X size={18} />
            </AnimatedSidebarClose>
          )}
        </div>
      </AnimatedSidebarHeader>
      <AnimatedSidebarContent>
        <nav aria-label="Menu administrativo">
          <AnimatedSidebarGroup>
            <AnimatedSidebarGroupLabel>Gerenciar</AnimatedSidebarGroupLabel>
            <AnimatedSidebarMenu>
              {navigation.map(({ path, label, icon: Icon }) => (
                <AnimatedSidebarMenuItem key={path}>
                  <AnimatedSidebarMenuButton
                    icon={<Icon size={18} />}
                    isActive={pathname.startsWith(path)}
                    onSelect={() => navigate(path)}
                    className={pathname.startsWith(path) ? "text-accent" : undefined}
                  >
                    {label}
                  </AnimatedSidebarMenuButton>
                </AnimatedSidebarMenuItem>
              ))}
            </AnimatedSidebarMenu>
          </AnimatedSidebarGroup>
        </nav>
      </AnimatedSidebarContent>
      <AnimatedSidebarFooter>
        <div className="flex items-center gap-3 px-1 py-2">
          <span className="grid size-8 shrink-0 place-items-center rounded-full border border-border bg-card">
            <ShieldCheck size={16} aria-hidden />
          </span>
          {expanded && (
            <div className="min-w-0">
              <p className="truncate text-sm font-medium">
                {session.data?.name ?? "Administrador"}
              </p>
              <p className="text-xs text-muted-foreground">Administrador</p>
            </div>
          )}
        </div>
        <Button
          variant="ghost"
          className="justify-start rounded-lg px-2"
          aria-label="Sair da conta"
          disabled={logout.isPending}
          onClick={() => logout.mutate()}
        >
          <LogOut size={16} aria-hidden />
          {expanded && (logout.isPending ? "Saindo…" : "Sair da conta")}
        </Button>
        {logout.error && (
          <AlertMessage tone="error">
            Não foi possível sair. Tente novamente.
          </AlertMessage>
        )}
      </AnimatedSidebarFooter>
    </AnimatedSidebar>
  );
}

export function AdminLayout(): React.JSX.Element {
  const queryClient = useQueryClient();
  const { pathname } = useLocation();
  useQuery({
    queryKey: ["contest"],
    queryFn: () => api.get<ContestStateDto>("/contest"),
    refetchInterval: 60_000,
  });
  const health = useQuery({
    queryKey: ["health"],
    queryFn: () => api.get<HealthDto>("/admin/health"),
    refetchInterval: 15_000,
  });
  const connection = useEventStream("/api/stream", {
    onContest: (state) => queryClient.setQueryData(["contest"], state),
    onScoreboard: (board) => queryClient.setQueryData(["admin-scoreboard"], board),
    onSubmission: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin-submissions"] });
      void queryClient.invalidateQueries({ queryKey: ["queue"] });
    },
  });
  const services =
    health.data && !health.isError
      ? [
          { label: "Banco de dados", ok: health.data.postgres.ok },
          { label: "Fila", ok: health.data.queue.ok },
          { label: "Judge0", ok: health.data.judge0.ok },
        ]
      : [];
  return (
    <AnimatedSidebarProvider
      className="admin-shell"
      style={{ "--sidebar-width": "15rem" }}
    >
      <a
        href="#admin-content"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-50 focus:bg-primary focus:p-3 focus:text-primary-foreground"
      >
        Pular para o conteúdo
      </a>
      <AdminSidebar />
      <AnimatedSidebarInset>
        <header className="flex min-h-20 flex-wrap items-center justify-between gap-3 border-b border-border px-4 py-4 sm:px-8">
          <div className="flex items-center gap-3">
            <AnimatedSidebarTrigger aria-label="Abrir ou recolher navegação">
              <PanelLeft size={18} aria-hidden />
            </AnimatedSidebarTrigger>
            <span className="hidden text-sm text-muted-foreground sm:inline">
              Administração
            </span>
            <span className="hidden text-muted-foreground sm:inline">/</span>
            <span className="text-sm">
              {navigation.find((item) => pathname.startsWith(item.path))?.label ??
                "Administração"}
            </span>
          </div>
          <span
            className="flex items-center gap-2 text-xs text-muted-foreground"
            role="status"
          >
            <span
              className={`size-1.5 rounded-full ${connection === "open" ? "bg-accent" : "bg-muted-foreground"}`}
            />
            {connection === "open"
              ? "Conectado em tempo real"
              : "Reconectando ao servidor"}
          </span>
        </header>
        <div
          id="admin-content"
          className="mx-auto w-full max-w-[1440px] flex-1 px-4 py-8 sm:px-8 lg:px-10"
        >
          <Outlet />
        </div>
        <footer className="mx-4 flex flex-wrap items-center gap-x-5 gap-y-2 border-t border-border py-5 text-xs text-muted-foreground sm:mx-8 lg:mx-10">
          <span>Serviços</span>
          {services.length ? (
            services.map((service) => (
              <span key={service.label} className="inline-flex items-center gap-1.5">
                {service.ok ? (
                  <CircleCheck size={13} className="text-accent" />
                ) : (
                  <CircleAlert size={13} className="text-destructive" />
                )}
                {service.label}
                <span className="sr-only">
                  {service.ok ? " disponível" : " indisponível"}
                </span>
              </span>
            ))
          ) : (
            <span>
              {health.isPending
                ? "Verificando serviços…"
                : "Status dos serviços indisponível"}
            </span>
          )}
        </footer>
      </AnimatedSidebarInset>
    </AnimatedSidebarProvider>
  );
}
